import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { osculatingOrbit, type BodyId } from "@solar/ephemeris";
import { useScene } from "../store";
import { bodyLive } from "./bodyState";
import { frame } from "./frame";
import { orbitWorldPoints } from "./sceneData";

const N = 192;

/**
 * 密切椭圆轨道线（ADR-0003 补充）。折线以绝对显示坐标（float64）缓存，
 * 每帧减去浮动原点后写入 GPU 缓冲，所以放大到地球附近时轨道线依然精确穿过天体。
 */
export function OrbitLine({ id }: { id: BodyId }) {
  const hidden = useScene((s) => s.hidden.has(id) || (id === "moon" && s.hidden.has("earth")));
  const show = useScene((s) => (id === "moon" ? s.layers.moonOrbit : s.layers.orbit));
  const selected = useScene((s) => s.selected === id);
  const attr = useMemo(() => new THREE.BufferAttribute(new Float32Array((N + 1) * 3), 3), []);
  const geo = useMemo(() => { const g = new THREE.BufferGeometry(); g.setAttribute("position", attr); return g; }, [attr]);
  const mat = useMemo(() => new THREE.LineBasicMaterial({ color: id === "moon" ? 0xffffff : 0x96b9ff, transparent: true, opacity: selected ? 0.7 : id === "moon" ? 0.45 : 0.3, depthWrite: false }), [id, selected]);
  const line = useMemo(() => { const l = new THREE.Line(geo, mat); l.frustumCulled = false; l.raycast = () => null; return l; }, [geo, mat]);
  const cache = useRef<{ key: string; pts: Float64Array } | null>(null);
  const showTrail = useScene((s) => s.layers.trail) && id !== "moon";
  const TRAIL = Math.round(N * 0.1) + 1;
  const trailAttr = useMemo(() => new THREE.BufferAttribute(new Float32Array(TRAIL * 3), 3), [TRAIL]);
  const trailCol = useMemo(() => { const c = new Float32Array(TRAIL * 3); for (let k = 0; k < TRAIL; k++) { const f = Math.pow(1 - k / (TRAIL - 1), 1.6); c[k * 3] = 0.62 * f; c[k * 3 + 1] = 0.82 * f; c[k * 3 + 2] = 1.0 * f; } return new THREE.BufferAttribute(c, 3); }, [TRAIL]);
  const trailLine = useMemo(() => {
    const g = new THREE.BufferGeometry(); g.setAttribute("position", trailAttr); g.setAttribute("color", trailCol);
    const l = new THREE.Line(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    l.frustumCulled = false; l.raycast = () => null; return l;
  }, [trailAttr, trailCol]);
  useEffect(() => { cache.current = null; }, [hidden, show, id]);

  useFrame(() => {
    if (hidden || (!show && !showTrail)) return;
    const s = useScene.getState(), m = frame.m, moon = id === "moon";
    const earth = moon ? bodyLive.get("earth") : null;
    // 绝对坐标只在 (时间, 模式插值) 变化时重算；月球还随显示后的地球移动
    const key = `${s.timeMs}|${m.toFixed(4)}`;
    if (!cache.current || cache.current.key !== key) {
      const shape = osculatingOrbit(id, new Date(s.timeMs), N);
      cache.current = { key, pts: orbitWorldPoints(shape.points, m, moon ? earth?.world : undefined) };
    }
    const pts = cache.current.pts, o = frame.origin;
    for (let i = 0; i <= N; i++) attr.setXYZ(i, pts[i * 3]! - o.x, pts[i * 3 + 1]! - o.y, pts[i * 3 + 2]! - o.z);
    attr.needsUpdate = true;
    geo.boundingSphere = null;
    // 拖尾：轨道上天体身后的一段弧（沿运动反方向，椭圆参数递增即运动方向），首点取天体当前位置
    const live = bodyLive.get(id);
    if (showTrail && live) {
      let best = 0, bd = Infinity;
      for (let i = 0; i < N; i++) { const dx = pts[i * 3]! - live.world.x, dy = pts[i * 3 + 1]! - live.world.y, dz = pts[i * 3 + 2]! - live.world.z, d = dx * dx + dy * dy + dz * dz; if (d < bd) { bd = d; best = i; } }
      trailAttr.setXYZ(0, live.world.x - o.x, live.world.y - o.y, live.world.z - o.z);
      for (let k = 1; k < TRAIL; k++) { const i = (best - k + N) % N; trailAttr.setXYZ(k, pts[i * 3]! - o.x, pts[i * 3 + 1]! - o.y, pts[i * 3 + 2]! - o.z); }
      trailAttr.needsUpdate = true; trailLine.geometry.boundingSphere = null;
    }
  });
  if (hidden) return null;
  return (<>{show && <primitive object={line} />}{showTrail && <primitive object={trailLine} />}</>);
}
